import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { setMyHandle } from '@/lib/me-api';
import { FormField } from '@/components/FormField';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { EMPTY } from '@/lib/formatters';

const HANDLE = /^[\p{L}\p{N}][\p{L}\p{N}._-]{0,23}$/u;

/**
 * Your handle: your @mention name on tasks, unique across the factory (PUT /api/me/handle). The
 * server says when someone else has it already.
 */
export function HandleCell({ handle, onSaved }: { handle: string | null; onSaved?: (handle: string) => void }) {
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const value = draft ?? handle ?? '';
  const trimmed = value.trim();
  const error = !trimmed ? null : !HANDLE.test(trimmed) ? 'Letters and numbers, up to 24, no spaces' : serverError;

  const save = async () => {
    if (saving) return;
    setSaving(true);
    const res = await setMyHandle(trimmed);
    setSaving(false);
    if (res.error || !res.data) {
      if (res.code === 'HANDLE_TAKEN' || res.code === 'INVALID_HANDLE') setServerError(res.error ?? 'Choose another handle');
      else toast.error(res.error ?? 'The handle was not saved');
      return;
    }
    onSaved?.(res.data.handle);
    setDraft(null);
    toast.success(`Handle saved: @${res.data.handle}.`);
  };

  return (
    <span className="inline-flex items-center gap-1">
      <span className="font-mono text-xs">{handle ? `@${handle}` : EMPTY}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label="Edit your handle"
        data-hint="Edit your handle"
        onClick={() => {
          setServerError(null);
          setDraft(handle ?? '');
        }}
      >
        <Pencil />
      </Button>
      <Dialog open={draft !== null} onOpenChange={(open) => !open && setDraft(null)}>
        <DialogContent className="sm:max-w-sm">
          {/* A form, so Enter submits as the main button does. */}
          <form
            noValidate
            className="contents"
            onSubmit={(e) => {
              e.preventDefault();
              if (!saving && trimmed && !error && trimmed !== handle) void save();
            }}
          >
            <DialogHeader>
              <DialogTitle>Your handle</DialogTitle>
            </DialogHeader>
            <FormField
              id="my-handle"
              label="Handle"
              info="Your @mention name on tasks. Unique across the factory."
              error={error}
            >
              <Input
                id="my-handle"
                value={value}
                onChange={(e) => {
                  setServerError(null);
                  setDraft(e.target.value);
                }}
                autoComplete="off"
              />
            </FormField>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDraft(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!trimmed || Boolean(error) || trimmed === handle} loading={saving}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </span>
  );
}
